import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PhotoCameraBackIcon from '@mui/icons-material/PhotoCameraBack';
import { useContext, useMemo, useState } from 'react';
import { APIContext } from '../../Contexts/APIContext';
import MultiFileUpload from '../../Utilities/MultiFileUpload';

interface IProps {
    open: boolean;
    onClose: () => void;
}

export default function UploadSitePhotosDialog({ open, onClose }: IProps) {
    const { post, showMessage, loading } = useContext(APIContext);
    const [files, setFiles] = useState<File[]>([]);

    const canUpload = useMemo(() => {
        return files.length > 0 && !loading;
    }, [files, loading]);

    const onFilesDrop = (incomingFiles: File[]) => {
        const accepted: File[] = [];
        const rejectedNames: string[] = [];

        for (const file of incomingFiles) {
            const fileName = file.name.trim();
            const extensionIndex = fileName.lastIndexOf('.');
            const extension = extensionIndex > -1 ? fileName.slice(extensionIndex).toLowerCase() : '';
            if (extension !== '.jpg') {
                rejectedNames.push(file.name);
                continue;
            }

            accepted.push(file);
        }

        if (rejectedNames.length > 0) {
            showMessage('warning', `Only .jpg files are allowed. Rejected: ${rejectedNames.join(', ')}`);
        }

        if (accepted.length === 0) {
            return;
        }

        setFiles((prev) => {
            const existingNames = new Set(prev.map((file) => file.name.toLowerCase()));
            const deduped = accepted.filter((file) => !existingNames.has(file.name.toLowerCase()));
            const duplicateCount = accepted.length - deduped.length;

            if (duplicateCount > 0) {
                showMessage('info', `${duplicateCount} duplicate file name(s) were ignored.`);
            }

            return [...prev, ...deduped];
        });
    };

    const onFileDelete = (fileName: string) => {
        setFiles((prev) => prev.filter((file) => file.name !== fileName));
    };

    const closeDialog = () => {
        setFiles([]);
        onClose();
    };

    const uploadFiles = () => {
        if (files.length === 0) {
            showMessage('warning', 'Please add one or more .jpg files.');
            return;
        }

        const formData = new FormData();
        files.forEach((file) => {
            formData.append('files', file);
        });

        post('SiteUploads', 'UploadSitePhotos', formData, () => {
            showMessage('success', `${files.length} photo(s) uploaded successfully.`);
            closeDialog();
        });
    };

    return (
        <Dialog
            open={open}
            onClose={closeDialog}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <PhotoCameraBackIcon color="primary" />
                    <Typography variant="h6">Upload Site Photos</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Upload one photo per site. The file name must be the site reference.
                    </Typography>
                    <Alert severity="info">
                        <Typography variant="body2">Photo requirements:</Typography>
                        <Box component="ul" sx={{ my: 0.5, pl: 2 }}>
                            <li>Only <code>.jpg</code> files are allowed (<code>.jpeg</code> is not accepted).</li>
                            <li>Each file name must be a site reference (with or without hyphens).</li>
                            <li>Each site should only have one photo.</li>
                            <li>Example: <code>A1-01-A-01.jpg</code> or <code>A101A01.jpg</code>.</li>
                        </Box>
                    </Alert>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <MultiFileUpload
                            files={files}
                            onDrop={onFilesDrop}
                            onDelete={onFileDelete}
                            acceptedTypes=".jpg"
                        />
                    </Paper>
                    {files.length > 0 &&
                        <Typography variant="body2" color="text.secondary">
                            {files.length} file(s) ready to upload.
                        </Typography>
                    }
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={closeDialog}>Cancel</Button>
                <Button variant="contained" onClick={uploadFiles} disabled={!canUpload}>
                    Upload Photos
                </Button>
            </DialogActions>
        </Dialog>
    );
}
